import { db } from '../firebase';
import { collection, query, where, getDocs, writeBatch, doc, serverTimestamp, addDoc, getDoc } from 'firebase/firestore';
import { CashTip } from './cashTipService';
import Papa from 'papaparse';
import { getSettings } from './settingsService';
import { getLocalDate, getFormattedTimestamp, parseTransactionDate } from '../lib/utils';
import { getRolePercentages } from './roleService';
import { isAllocated, isPos, filterTransactionsByLocation, matchesLocation } from '../lib/transactionUtils';
import { MasterTransaction, ShortfallAllocation } from '../types';

export const getPayrollSummary = async (tenantId: string, startDate?: string, endDate?: string, locationId?: string) => {
  const allocationsRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const settingsRef = doc(db, 'tenants', tenantId, 'settings', 'config');
  const staffRef = collection(db, 'tenants', tenantId, 'staff');
  const shortfallRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');
  
  const [snapshot, settingsDoc, staffSnapshot, shortfallSnapshot] = await Promise.all([
    getDocs(allocationsRef),
    getDoc(settingsRef),
    getDocs(staffRef),
    getDocs(shortfallRef)
  ]);
  
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const posTipHandling = settings.posTipHandling || 'Unpaid';
  
  const staffMap: Record<string, any> = {};
  staffSnapshot.docs.forEach(doc => {
    const s = doc.data();
    staffMap[s.email] = s;
  });

  const roles = await getRolePercentages(tenantId);
  const eligibleRoles = new Set(roles.filter(r => r.isEligible !== false).map(r => r.role));

  const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
  
  // Conforms strictly to TRANSACTION_LOGIC.md system contract (filters out Short-Fall from master stream to avoid double-counting with manual shortfall loading)
  let filtered = allDocs.filter(t => isAllocated(t, allDocs) && t.source !== 'Short-Fall');

  // Location filter
  if (locationId && locationId !== 'all') {
    filtered = filterTransactionsByLocation(filtered, locationId);
  }
  
  if (startDate || endDate) {
    filtered = filtered.filter(t => {
      const transactionDate = parseTransactionDate(t.tipDate || t.timestamp);
      if (startDate && transactionDate < startDate) return false;
      if (endDate && transactionDate > endDate) return false;
      return true;
    });
  }

  let total = 0;
  const uniqueStaff = new Set<string>();
  
  filtered.forEach(data => {
    // Eligibility Filter
    const staffInfo = staffMap[data.email] || {};
    if (!eligibleRoles.has(staffInfo.role)) return;

    // Filters
    if (posTipHandling === 'Reported' && isPos(data, allDocs)) return;
    if (data.submissionStatus?.toLowerCase().trim() === 'rejected') return;

    total += data.amount || 0;
    if (data.email) {
      uniqueStaff.add(data.email);
    }
  });

  // Include shortfall allocations
  let shortfallAllocations = shortfallSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShortfallAllocation));
  
  // Location filter for shortfall
  if (locationId && locationId !== 'all') {
    shortfallAllocations = shortfallAllocations.filter(sf => {
      if (sf.locationId) return sf.locationId === locationId;
      return locationId === 'loc-downtown' || locationId === tenantId;
    });
  }

  if (startDate || endDate) {
    shortfallAllocations = shortfallAllocations.filter(sf => {
      if (startDate && sf.startDate < startDate) return false;
      if (endDate && sf.endDate > endDate) return false;
      return true;
    });
  }

  shortfallAllocations.forEach(sf => {
    total += sf.allocatedShortfall || 0;
    if (sf.email) {
      uniqueStaff.add(sf.email);
    }
  });
  
  return { total: total.toFixed(2), count: uniqueStaff.size };
};

export const getPayrollDetails = async (tenantId: string, startDate: string, endDate: string, locationId?: string) => {
  const allocationsRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const staffRef = collection(db, 'tenants', tenantId, 'staff');
  const tipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
  const settingsRef = doc(db, 'tenants', tenantId, 'settings', 'config');
  const shortfallRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');

  const [snapshot, staffSnapshot, tipsSnapshot, settingsDoc, shortfallSnapshot] = await Promise.all([
    getDocs(allocationsRef),
    getDocs(staffRef),
    getDocs(query(tipsRef, where('tipDate', '>=', startDate), where('tipDate', '<=', endDate))),
    getDoc(settingsRef),
    getDocs(shortfallRef)
  ]);
  
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const posTipHandling = settings.posTipHandling || 'Unpaid';

  const staffMap: Record<string, any> = {};
  staffSnapshot.docs.forEach(doc => {
    const s = doc.data();
    staffMap[s.email] = s;
  });
  
  const tipPaymentInfo: Record<string, string> = {};
  tipsSnapshot.docs.forEach(doc => {
    const t = doc.data();
    tipPaymentInfo[t.email] = t.paymentStatus || 'Unpaid';
  });
  
  const reportMap: Record<string, { 
    name: string, 
    posId: string, 
    email: string,
    amount: number, 
    serviceChargeAmount: number,
    paymentMethod: string,
    startDate: string,
    endDate: string,
    status: string,
    locationId?: string,
    locationName?: string,
    transactions: any[]
  }> = {};
  
  const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
  
  // Filter using central isAllocated helper conforming to TRANSACTION_LOGIC (filters out Short-Fall from master stream to avoid double-counting with manual shortfall loading)
  let filtered = allDocs.filter(t => isAllocated(t, allDocs) && t.source !== 'Short-Fall');
  
  // Location filter
  if (locationId && locationId !== 'all') {
    filtered = filterTransactionsByLocation(filtered, locationId);
  }

  if (startDate || endDate) {
    filtered = filtered.filter(t => {
      const transactionDate = parseTransactionDate(t.tipDate || t.timestamp);
      if (startDate && transactionDate < startDate) return false;
      if (endDate && transactionDate > endDate) return false;
      return true;
    });
  }

  filtered.forEach(data => {
    // Safety Filter: Respect POS Tip Handling Setting
    if (posTipHandling === 'Reported' && isPos(data, allDocs)) return;
    if (data.submissionStatus?.toLowerCase().trim() === 'rejected') return;

    // Prioritize staff internal data, fallback to transaction data
    const staffInfo = staffMap[data.email] || {};
    const posId = staffInfo.posId || 'N/A';
    const email = data.email || 'N/A';
    const isAlreadyPaid = (data.payoutMethod && data.payoutMethod.toLowerCase().trim() !== 'paycheck') || tipPaymentInfo[data.email] === 'Paid';
    const paymentMethod = isAlreadyPaid ? `Already Paid (${data.payoutMethod || 'Other'})` : 'Paycheck';
    const rowLocationId = data.locationId || staffInfo.locationId || 'loc-downtown';
    const rowLocationName = data.locationName || staffInfo.locationName || 'Downtown Bistro';
    
    const key = `${data.email}_${paymentMethod}_${rowLocationId}`;
    
    if (!reportMap[key]) {
      reportMap[key] = { 
        name: data.staffName || staffInfo.name || 'Unknown', 
        posId,
        email,
        amount: 0, 
        serviceChargeAmount: 0,
        paymentMethod,
        startDate,
        endDate,
        status: 'Approved',
        locationId: rowLocationId,
        locationName: rowLocationName,
        transactions: []
      };
    }
    const amount = data.amount || 0;
    if (data.isServiceCharge) {
      reportMap[key].serviceChargeAmount += amount;
    } else {
      reportMap[key].amount += amount;
    }
    
    let approvedAtRaw = data.auditTimestamp || data.timestamp || null;
    let approvedAtStr = 'N/A';
    if (approvedAtRaw && typeof approvedAtRaw.toDate === 'function') {
      approvedAtStr = approvedAtRaw.toDate().toLocaleString();
    }

    reportMap[key].transactions.push({
      id: data.id,
      amount,
      tipDate: data.tipDate,
      shift: data.shift || 'N/A',
      type: data.type || 'Allocated',
      note: (data as any).note || '',
      approvedBy: data.auditActor || 'System',
      approvedAt: approvedAtStr,
      locationId: rowLocationId,
      locationName: rowLocationName
    });
  });

  // Include shortfall allocations
  let shortfallAllocations = shortfallSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShortfallAllocation));
  
  // Location filter for shortfall
  if (locationId && locationId !== 'all') {
    shortfallAllocations = shortfallAllocations.filter(sf => {
      if (sf.locationId) return sf.locationId === locationId;
      return locationId === 'loc-downtown' || locationId === tenantId;
    });
  }

  shortfallAllocations = shortfallAllocations.filter(sf => {
    if (startDate && sf.startDate < startDate) return false;
    if (endDate && sf.endDate > endDate) return false;
    return true;
  });

  shortfallAllocations.forEach(sf => {
    const staffInfo = staffMap[sf.email] || {};
    const posId = staffInfo.posId || 'N/A';
    const email = sf.email || 'N/A';
    const paymentMethod = 'Paycheck';
    const rowLocationId = sf.locationId || staffInfo.locationId || 'loc-downtown';
    const rowLocationName = sf.locationName || staffInfo.locationName || 'Downtown Bistro';
    const key = `${sf.email}_${paymentMethod}_${rowLocationId}`;
    
    if (!reportMap[key]) {
      reportMap[key] = { 
        name: sf.staffName || staffInfo.name || 'Unknown', 
        posId,
        email,
        amount: 0, 
        serviceChargeAmount: 0,
        paymentMethod,
        startDate,
        endDate,
        status: 'Approved',
        locationId: rowLocationId,
        locationName: rowLocationName,
        transactions: []
      };
    }
    const amount = sf.allocatedShortfall || 0;
    reportMap[key].amount += amount;
    
    let approvedAtStr = sf.createdAt ? (sf.createdAt.toDate ? sf.createdAt.toDate().toLocaleString() : new Date(sf.createdAt).toLocaleString()) : 'N/A';

    reportMap[key].transactions.push({
      id: sf.id || 'N/A',
      amount,
      tipDate: `${sf.startDate} to ${sf.endDate}`,
      shift: 'Shortfall Allocation',
      type: 'true_up',
      note: `Safe-Harbor Shortfall (${sf.allocationMethod.replace('_', ' ')})`,
      approvedBy: 'System',
      approvedAt: approvedAtStr,
      locationId: rowLocationId,
      locationName: rowLocationName
    });
  });
  
  return Object.values(reportMap);
};

export const getLastPayrollExport = async (tenantId: string, locationId?: string) => {
  const exportsRef = collection(db, 'tenants', tenantId, 'payroll_exports');
  const q = query(
    exportsRef, 
    where('status', '==', 'Approved')
  );
  
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  
  let docs = snapshot.docs.map(d => d.data());
  if (locationId && locationId !== 'all') {
    docs = docs.filter(d => d.locationId === locationId || (!d.locationId && (locationId === 'loc-downtown' || locationId === tenantId)));
  }

  if (docs.length === 0) return null;

  // Sort manually since we might not have a composite index on dateApproved
  docs.sort((a, b) => 
    (b.dateApproved || '').localeCompare(a.dateApproved || '')
  );
  
  return docs[0];
};

export const approvePayroll = async (
  tenantId: string,
  startDate: string,
  endDate: string,
  managerEmail: string,
  locationId?: string,
  locationName?: string
) => {
  console.log('Approving payroll for tenant:', tenantId, 'range:', startDate, 'to', endDate, 'location:', locationId);
  const batch = writeBatch(db);
  const settings = await getSettings(tenantId);
  const timeZone = settings?.timezone || 'UTC';
  const today = getLocalDate(timeZone);

  // 1. Create Export References
  const payrollExportRef = doc(collection(db, 'tenants', tenantId, 'payroll_exports'));
  const exportId = payrollExportRef.id;
  
  // 2. Fetch and Link all "Allocated" transactions in this range
  const allocationsRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const shortfallRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');
  
  const [snapshot, shortfallSnapshot] = await Promise.all([
    getDocs(allocationsRef),
    getDocs(shortfallRef)
  ]);
  
  const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
  
  // Filter matching TRANSACTION_LOGIC contract (filters out Short-Fall from master stream to avoid double-counting with manual shortfall loading)
  let filtered = allDocs.filter(t => isAllocated(t, allDocs) && t.source !== 'Short-Fall');

  // Location scoping
  if (locationId && locationId !== 'all') {
    filtered = filterTransactionsByLocation(filtered, locationId);
  }
  
  filtered = filtered.filter(t => {
    const transactionDate = parseTransactionDate(t.tipDate || t.timestamp);
    if (startDate && transactionDate < startDate) return false;
    if (endDate && transactionDate > endDate) return false;
    return true;
  });

  console.log('Found transactions to lock:', filtered.length);
  let totalTips = 0;
  filtered.forEach(data => {
    totalTips += data.amount || 0;
  });

  // Fetch and lock shortfall allocations
  let shortfallAllocations = shortfallSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShortfallAllocation));
  if (locationId && locationId !== 'all') {
    shortfallAllocations = shortfallAllocations.filter(sf => {
      if (sf.locationId) return sf.locationId === locationId;
      return locationId === 'loc-downtown' || locationId === tenantId;
    });
  }

  shortfallAllocations = shortfallAllocations.filter(sf => {
    if (startDate && sf.startDate < startDate) return false;
    if (endDate && sf.endDate > endDate) return false;
    return true;
  });

  let totalShortfall = 0;
  shortfallAllocations.forEach(sf => {
    totalShortfall += sf.allocatedShortfall || 0;
  });

  const grandTotal = totalTips + totalShortfall;

  const targetLocationId = locationId && locationId !== 'all' ? locationId : 'all';
  const targetLocationName = locationName || (targetLocationId === 'all' ? 'All Locations (Consolidated)' : 'Downtown Bistro');

  console.log('Saving export to payroll_exports:', payrollExportRef.id);
  batch.set(payrollExportRef, {
    tenantId,
    startDate,
    endDate,
    dateApproved: today,
    approvedBy: managerEmail,
    status: 'Approved',
    total: grandTotal.toFixed(2),
    totalTips: totalTips.toFixed(2),
    totalShortfall: totalShortfall.toFixed(2),
    count: filtered.length + shortfallAllocations.length,
    locationId: targetLocationId,
    locationName: targetLocationName
  });

  const exportRecordRef = doc(collection(db, 'tenants', tenantId, 'payroll_exports'));
  console.log('Saving export to payroll_exports:', exportRecordRef.id);
  batch.set(exportRecordRef, {
    tenantId,
    timestamp: serverTimestamp(),
    type: 'Payroll',
    period: `${startDate} to ${endDate}`,
    startDate,
    endDate,
    approvedBy: managerEmail,
    status: 'Approved',
    exportId, // Link back to the raw export
    totalTips: grandTotal.toFixed(2),
    standardTips: totalTips.toFixed(2),
    shortfallTips: totalShortfall.toFixed(2),
    paycheckTips: 'N/A',
    locationId: targetLocationId,
    locationName: targetLocationName
  });

  // 3. Link records
  filtered.forEach(data => {
    const txDocRef = doc(db, 'tenants', tenantId, 'master_transactions', data.id);
    batch.update(txDocRef, {
      payrollIncluded: true,
      payrollExportId: exportId
    });
  });

  // 4. Link shortfall records
  shortfallAllocations.forEach(sf => {
    const sfDocRef = doc(db, 'tenants', tenantId, 'shortfall_allocations', sf.id!);
    batch.update(sfDocRef, {
      payrollIncluded: true,
      payrollExportId: exportId
    });
  });

  try {
    console.log('Committing batch...');
    await batch.commit();
    console.log('Batch committed successfully');
  } catch (err) {
    console.error('Batch commit failed:', err);
    throw err;
  }
};

export const generatePayrollCSV = async (
  tenantId: string, 
  startDate: string, 
  endDate: string,
  locationId?: string
): Promise<string> => {
  const allocationsRef = collection(db, 'tenants', tenantId, 'master_transactions');
  const staffRef = collection(db, 'tenants', tenantId, 'staff');
  const tipsRef = collection(db, 'tenants', tenantId, 'cash_tips');
  const settingsRef = doc(db, 'tenants', tenantId, 'settings', 'config');
  const shortfallRef = collection(db, 'tenants', tenantId, 'shortfall_allocations');
  
  const [snapshot, staffSnapshot, tipsSnapshot, settingsDoc, shortfallSnapshot] = await Promise.all([
    getDocs(allocationsRef),
    getDocs(staffRef),
    getDocs(query(tipsRef, where('tipDate', '>=', startDate), where('tipDate', '<=', endDate))),
    getDoc(settingsRef),
    getDocs(shortfallRef)
  ]);
  
  const settings = settingsDoc.exists() ? settingsDoc.data() : {};
  const provider = settings.payrollProvider || 'Standard';
  const posTipHandling = settings.posTipHandling || 'Unpaid';

  const staffLookup: Record<string, any> = {};
  staffSnapshot.docs.forEach(doc => {
    const s = doc.data();
    if (s.email) staffLookup[s.email] = s;
  });

  const tipPaymentInfo: Record<string, 'Paid' | 'Unpaid'> = {};
  tipsSnapshot.docs.forEach(doc => {
    const t = doc.data();
    if (t.email) tipPaymentInfo[t.email] = t.paymentStatus;
  });
  
  // Group results by Email + Location to consolidate tips per staff member per branch
  const aggregation: Record<string, { 
    name: string, 
    email: string, 
    posId: string, 
    locationId: string,
    locationName: string,
    paycheckAmount: number, 
    cashAmount: number,
    serviceChargeAmount: number
  }> = {};

  const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as MasterTransaction));
  
  // Filter conforming to TRANSACTION_LOGIC (filters out Short-Fall from master stream to avoid double-counting with manual shortfall loading)
  let filtered = allDocs.filter(t => isAllocated(t, allDocs) && t.source !== 'Short-Fall');
  
  // Location filtering
  if (locationId && locationId !== 'all') {
    filtered = filterTransactionsByLocation(filtered, locationId);
  }

  filtered = filtered.filter(t => {
    const transactionDate = parseTransactionDate(t.tipDate || t.timestamp);
    if (startDate && transactionDate < startDate) return false;
    if (endDate && transactionDate > endDate) return false;
    return true;
  });

  filtered.forEach(d => {
    // Safety Filter: Respect POS Tip Handling Setting
    if (posTipHandling === 'Reported' && isPos(d, allDocs)) return;
    if (d.submissionStatus?.toLowerCase().trim() === 'rejected') return;

    const email = d.email || 'unknown';
    const staffInfo = staffLookup[email] || {};
    const rowLocationId = d.locationId || staffInfo.locationId || 'loc-downtown';
    const rowLocationName = d.locationName || staffInfo.locationName || 'Downtown Bistro';
    
    const payoutMethod = (d.payoutMethod || 'Paycheck').toLowerCase().trim();
    const isCash = payoutMethod === 'cash';

    const groupKey = `${email}_${rowLocationId}`;

    if (!aggregation[groupKey]) {
      aggregation[groupKey] = {
        name: d.staffName || staffInfo.name || 'Unknown',
        email: email,
        posId: staffInfo.posId || 'N/A',
        locationId: rowLocationId,
        locationName: rowLocationName,
        paycheckAmount: 0,
        cashAmount: 0,
        serviceChargeAmount: 0
      };
    }
    
    if (d.isServiceCharge) {
       aggregation[groupKey].serviceChargeAmount += d.amount || 0;
    } else if (isCash) {
       aggregation[groupKey].cashAmount += d.amount || 0;
    } else {
       aggregation[groupKey].paycheckAmount += d.amount || 0;
    }
  });

  // Aggregate shortfall allocations
  let shortfallAllocations = shortfallSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ShortfallAllocation));
  if (locationId && locationId !== 'all') {
    shortfallAllocations = shortfallAllocations.filter(sf => {
      if (sf.locationId) return sf.locationId === locationId;
      return locationId === 'loc-downtown' || locationId === tenantId;
    });
  }

  shortfallAllocations = shortfallAllocations.filter(sf => {
    if (startDate && sf.startDate < startDate) return false;
    if (endDate && sf.endDate > endDate) return false;
    return true;
  });

  shortfallAllocations.forEach(sf => {
    const email = sf.email || 'unknown';
    const staffInfo = staffLookup[email] || {};
    const rowLocationId = sf.locationId || staffInfo.locationId || 'loc-downtown';
    const rowLocationName = sf.locationName || staffInfo.locationName || 'Downtown Bistro';
    const groupKey = `${email}_${rowLocationId}`;
    
    if (!aggregation[groupKey]) {
      aggregation[groupKey] = {
        name: sf.staffName || staffInfo.name || 'Unknown',
        email: email,
        posId: staffInfo.posId || 'N/A',
        locationId: rowLocationId,
        locationName: rowLocationName,
        paycheckAmount: 0,
        cashAmount: 0,
        serviceChargeAmount: 0
      };
    }
    
    aggregation[groupKey].paycheckAmount += sf.allocatedShortfall || 0;
  });
  
  const staffArray = Object.values(aggregation);
  let csvData: any[] = [];

  const timeZone = settings.timezone || 'UTC';
  const period = `${startDate} to ${endDate}`;
  const timestamp = getFormattedTimestamp(timeZone);

  // Provider Specific Mapping Logic (With Location Tags for Department Cost-Center Routing)
  if (provider === 'Gusto') {
    csvData = staffArray.map(s => {
      const names = s.name.split(' ');
      return {
        'First Name': names[0] || '',
        'Last Name': names.slice(1).join(' ') || '',
        'Email': s.email,
        'Location Name': s.locationName,
        'Location ID': s.locationId,
        'Tips': s.paycheckAmount.toFixed(2),
        'Cash Tips': s.cashAmount.toFixed(2),
        'Service Charges': s.serviceChargeAmount.toFixed(2),
        'Timestamp': timestamp,
        'Period': period,
        'Source': 'TipValue AI'
      };
    });
  } else if (provider === 'ADP') {
    staffArray.forEach(s => {
      if (s.paycheckAmount > 0) {
        csvData.push({
          'Employee ID': s.posId,
          'Company Code': settings.adpCompanyCode || '',
          'Cost Center / Location': s.locationName,
          'Location ID': s.locationId,
          'Earnings Code': 'TIPS',
          'Amount': s.paycheckAmount.toFixed(2),
          'Period Start': startDate,
          'Period End': endDate,
          'Timestamp': timestamp,
          'Period': period,
          'Source': 'TipValue AI'
        });
      }
      if (s.serviceChargeAmount > 0) {
        csvData.push({
          'Employee ID': s.posId,
          'Company Code': settings.adpCompanyCode || '',
          'Cost Center / Location': s.locationName,
          'Location ID': s.locationId,
          'Earnings Code': 'SERVICE-CHG',
          'Amount': s.serviceChargeAmount.toFixed(2),
          'Period Start': startDate,
          'Period End': endDate,
          'Timestamp': timestamp,
          'Period': period,
          'Source': 'TipValue AI'
        });
      }
    });
  } else if (provider === 'Paychex') {
    staffArray.forEach(s => {
      if (s.paycheckAmount > 0) {
        csvData.push({
          'Worker ID': s.posId,
          'Name': s.name,
          'Email': s.email,
          'Branch / Location': s.locationName,
          'Location ID': s.locationId,
          'Pay Component': 'Tips',
          'Value': s.paycheckAmount.toFixed(2),
          'Timestamp': timestamp,
          'Period': period,
          'Source': 'TipValue AI'
        });
      }
      if (s.serviceChargeAmount > 0) {
        csvData.push({
          'Worker ID': s.posId,
          'Name': s.name,
          'Email': s.email,
          'Branch / Location': s.locationName,
          'Location ID': s.locationId,
          'Pay Component': 'Service Charges',
          'Value': s.serviceChargeAmount.toFixed(2),
          'Timestamp': timestamp,
          'Period': period,
          'Source': 'TipValue AI'
        });
      }
    });
  } else if (provider === 'Justworks') {
    staffArray.forEach(s => {
      if (s.paycheckAmount > 0) {
        csvData.push({
          'Employee Email': s.email,
          'Location': s.locationName,
          'Location ID': s.locationId,
          'Amount': s.paycheckAmount.toFixed(2),
          'Payment Type': 'Supplemental',
          'Timestamp': timestamp,
          'Period': period,
          'Source': 'TipValue AI'
        });
      }
      if (s.serviceChargeAmount > 0) {
        csvData.push({
          'Employee Email': s.email,
          'Location': s.locationName,
          'Location ID': s.locationId,
          'Amount': s.serviceChargeAmount.toFixed(2),
          'Payment Type': 'Supplemental',
          'Timestamp': timestamp,
          'Period': period,
          'Source': 'TipValue AI'
        });
      }
    });
  } else {
    // Default / Standard Format
    csvData = staffArray.map(s => ({
      'Employee Name': s.name,
      'POSId': s.posId,
      'Email': s.email,
      'Store Location': s.locationName,
      'Location ID': s.locationId,
      'Paycheck Tips': s.paycheckAmount.toFixed(2),
      'Cash Tips (Already Paid)': s.cashAmount.toFixed(2),
      'Service Charges': s.serviceChargeAmount.toFixed(2),
      'Total Tips': (s.paycheckAmount + s.cashAmount).toFixed(2),
      'Period Start': startDate,
      'Period End': endDate,
      'Timestamp': timestamp,
      'Period': period,
      'Source': 'TipValue AI'
    }));
    
    const totalPaycheck = staffArray.reduce((acc, curr) => acc + curr.paycheckAmount, 0);
    const totalCash = staffArray.reduce((acc, curr) => acc + curr.cashAmount, 0);
    csvData.push({
      'Employee Name': 'TOTAL',
      'POSId': '',
      'Email': '',
      'Store Location': 'All Included',
      'Location ID': '',
      'Paycheck Tips': totalPaycheck.toFixed(2),
      'Cash Tips (Already Paid)': totalCash.toFixed(2),
      'Total Tips': (totalPaycheck + totalCash).toFixed(2),
      'Period Start': '',
      'Period End': '',
      'Timestamp': timestamp,
      'Period': period,
      'Source': 'TipValue AI'
    });
  }
  
  return Papa.unparse(csvData);
};

export const checkPayrollExportStatus = async (tenantId: string, startDate: string, endDate: string, locationId?: string) => {
  const exportsRef = collection(db, 'tenants', tenantId, 'payroll_exports');
  const q = query(
    exportsRef,
    where('startDate', '==', startDate),
    where('endDate', '==', endDate),
    where('status', '==', 'Approved')
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return false;
  
  if (!locationId || locationId === 'all') {
    return true;
  }
  
  return snapshot.docs.some(d => {
    const data = d.data();
    return data.locationId === locationId || (!data.locationId && (locationId === 'loc-downtown' || locationId === tenantId));
  });
};

